// WindowWeek (Domain/Schedule, BL-189; was apps/web BookWeek.tsx, BL-133 calendar B): a week of arrival
// windows to book a visit into. The windows run down, the days across; each cell says the technician
// time still open in it for the work picked: in ink when it fits, muted when nobody can fit the visit's
// length, hatched when full, Closed when the office closed it, blank once over (the one under way is
// still offered). The chosen cell is filled in ink. One tap picks the day and the window together;
// arrow keys move between cells (one tab stop), Enter or Space picks. Above it the soonest that fits,
// with a link that picks it, and Earlier and Later between the weeks; under it what the marks mean and
// a day to jump to. Draws from ready-to-show words (the app's book-model weekGrid). Reads only
// --wweek-*; Link and Field read their own.
import { useRef, useState, type KeyboardEvent } from 'react';
import { TextField } from '../Field/Field.tsx';
import { Link } from '../Link/Link.tsx';
import './WindowWeek.css';

/** What a cell says: open and fits, short (room, but the visit doesn't fit), full, closed, over, unknown. */
export type WindowCellState = 'open' | 'short' | 'full' | 'closed' | 'past' | 'unknown';

export interface WindowWeekCell {
  /** What it says: the hours open ("8 h"), nothing once over. */
  text: string;
  /** Its name in full, for screen readers: "Tomorrow, 2–4 p.m.: 8 h open, fits 1 h". */
  name: string;
  state: WindowCellState;
  /** The day and window chosen. */
  chosen?: boolean;
}
export interface WindowWeekDay {
  /** Its name in full: "Today", "Tomorrow", "Thu, Oct 29". */
  label: string;
  /** Its short name over the date: "Thu"; today's is "Today" where there's room. */
  day: string;
  /** Today's short name where there isn't ("Tue"). */
  short?: string;
  /** The date's number: "29". */
  num: string;
  today?: boolean;
  /** The chosen window is on this day. */
  chosen?: boolean;
}
export interface WindowWeekRow {
  /** "8–10 a.m.", in full, for screen readers. */
  label: string;
  /** "8–10" over "a.m.". */
  range: string;
  part: string;
  /** One cell a day, in the days' order. */
  cells: WindowWeekCell[];
}

export interface WindowWeekProps {
  /** The soonest that fits, in words ("Soonest that fits 1 h:"), with what picks it, or why there's none. */
  soonest: { words: string; pick?: string };
  onSoonest?: () => void;
  /** The week shown: "Oct 27 to Nov 2". */
  range: string;
  onEarlier?: () => void;
  onLater?: () => void;
  /** Nothing yet: still reading the open times. */
  days: WindowWeekDay[] | null;
  rows: WindowWeekRow[];
  /** Said over the grid when the open times didn't come (every window can still be picked). */
  failed?: string | null;
  /** What the marks mean, under it. */
  note: string;
  onPick: (row: number, col: number) => void;
  /** The day to jump to (a date field): the chosen day, the first and last that can be booked. */
  jump: { value: string; min: string; max: string; error?: string | null; onChange: (date: string) => void };
}

const MOVES: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

export function WindowWeek({
  soonest,
  onSoonest,
  range,
  onEarlier,
  onLater,
  days,
  rows,
  failed,
  note,
  onPick,
  jump,
}: WindowWeekProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState<{ range: string; row: number; col: number } | null>(null);
  const cols = days?.length ?? 0;
  // one cell takes Tab (a roving tab stop): the last one moved to on this week, else the chosen one,
  // else the first that fits, else the first
  const find = (ok: (c: WindowWeekCell) => boolean) => {
    for (let col = 0; col < cols; col++)
      for (let row = 0; row < rows.length; row++) if (ok(rows[row]!.cells[col]!)) return { row, col };
    return null;
  };
  const stop = (focus && focus.range === range ? focus : null) ??
    find((c) => !!c.chosen) ??
    find((c) => c.state === 'open') ?? { row: 0, col: 0 };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>('[data-cell]');
    const move = MOVES[e.key];
    if (!t || !move) return;
    const [row, col] = t.dataset.cell!.split(':').map(Number) as [number, number];
    const to = {
      row: Math.min(rows.length - 1, Math.max(0, row + move[0])),
      col: Math.min(cols - 1, Math.max(0, col + move[1])),
    };
    e.preventDefault();
    setFocus({ range, ...to });
    gridRef.current?.querySelector<HTMLElement>(`[data-cell="${to.row}:${to.col}"]`)?.focus();
  };
  return (
    <div className="fl-wweek">
      <p className="fl-wweek__soon">
        {soonest.words}
        {soonest.pick ? (
          <>
            {' '}
            <Link onPress={() => onSoonest?.()}>{soonest.pick}</Link>
          </>
        ) : null}
      </p>
      <div className="fl-wweek__head">
        <Link isDisabled={!onEarlier} onPress={() => (setFocus(null), onEarlier?.())}>
          Earlier
        </Link>
        <b className="fl-wweek__range" aria-live="polite">
          {range}
        </b>
        <Link isDisabled={!onLater} onPress={() => (setFocus(null), onLater?.())}>
          Later
        </Link>
      </div>
      {!days ? (
        <p className="fl-wweek__say">Getting the open times…</p>
      ) : (
        <>
          {failed ? <p className="fl-wweek__say">{failed}</p> : null}
          <div
            ref={gridRef}
            className="fl-wweek__grid"
            role="grid"
            aria-label={`Open time by arrival window, ${range}`}
            data-days={cols}
            onKeyDown={onKey}
          >
            <div role="row" className="fl-wweek__row">
              {/* the corner says nothing on screen, so a cell, not an empty header (axe empty-table-header) */}
              <span role="gridcell" className="fl-wweek__corner" aria-label="Arrival window" />
              {days.map((x) => (
                <span
                  key={x.label}
                  role="columnheader"
                  className="fl-wweek__day"
                  data-today={x.today ? '' : undefined}
                  data-chosen={x.chosen ? '' : undefined}
                  aria-label={x.label}
                >
                  <b>
                    {x.short ? (
                      <>
                        <span className="fl-wweek__wide">{x.day}</span>
                        <span className="fl-wweek__narrow">{x.short}</span>
                      </>
                    ) : (
                      x.day
                    )}
                  </b>
                  <span>{x.num}</span>
                </span>
              ))}
            </div>
            {rows.map((r, row) => (
              <div role="row" className="fl-wweek__row" key={r.label}>
                <span role="rowheader" className="fl-wweek__window" aria-label={r.label}>
                  {r.range}
                  <small>{r.part}</small>
                </span>
                {r.cells.map((c, col) => (
                  <span role="gridcell" key={col} className="fl-wweek__gc">
                    <button
                      type="button"
                      className="fl-wweek__cell"
                      data-state={c.state}
                      data-cell={`${row}:${col}`}
                      tabIndex={stop.row === row && stop.col === col ? 0 : -1}
                      aria-pressed={!!c.chosen}
                      aria-disabled={c.state === 'past' || undefined}
                      aria-label={c.name}
                      onFocus={() => setFocus({ range, row, col })}
                      onClick={() => c.state !== 'past' && onPick(row, col)}
                    >
                      {c.state === 'closed' ? <span className="fl-wweek__wide">Closed</span> : c.text}
                    </button>
                  </span>
                ))}
              </div>
            ))}
          </div>
          <p className="fl-wweek__say">{note}</p>
        </>
      )}
      <div className="fl-wweek__jump">
        <TextField
          label="Jump to a day"
          type="date"
          min={jump.min}
          max={jump.max}
          value={jump.value}
          onChange={(v) => v && jump.onChange(v)}
          error={jump.error ?? null}
        />
      </div>
    </div>
  );
}
